/* eslint-disable @typescript-eslint/no-namespace */
import { make, type Brand } from "ts-brand";
import {
    type ExternalQuoteEmailTemplateData,
    type ExternalEmailTemplateData,
} from "../../documents/ExternalEmailTemplateData";
import { type ServiceDefinition, type ServiceProvider } from "../configuration";
import { type EmailTemplateText } from "./EmailTemplateText";
import { Template } from "./Template";

export const templateNames = [
    "appointment-proposals-customer",
    "appointment-proposals-sales",
    "order-cancelled-by-customer-customer",
    "order-cancelled-by-customer-sales",
    "order-cancelled-by-sales-customer",
    "order-cancelled-by-sales-sales",
    "order-confirmation-customer",
    "order-confirmation-sales",
    "order-quote-request-customer",
    "order-quote-request-sales",
    "order-rejected-by-provider-customer",
    "order-rejected-by-provider-sales",
    "quote-document-customer",
    "quote-document-sales",
] as const;

// FIXME(Template): Replace with ExternalEmailTemplateData
export type EmailComponent = (template: ExternalEmailTemplateData) => string;
export type QuoteEmailComponent = (template: ExternalQuoteEmailTemplateData) => string;

export type EmailAttachment = {
    /**
     * The name of the attachment.
     */
    fileName: string;
    // TODO(Template): What's this id for?
    id: string;
};

export type EmailTemplateData = {
    /**
     * The name of the email template.
     */
    name: EmailTemplate.Name;
    /**
     * The subject of the email template.
     */
    subject: string;
    /**
     * The lead text of the email template.
     */
    leadText?: string;
    /**
     * The preview text of the email template (shown in email client preview panes).
     */
    preview?: string;
    /**
     * The attachments of the email template.
     */
    attachments?: EmailAttachment[];
    /**
     * The customizable text content for the email template (greetings, body text, labels, etc.).
     */
    text?: EmailTemplateText;
};

export type EmailTemplate = EmailTemplateData &
    Template & {
        /**
         * The id of the email template, which is the template name.
         */
        id: EmailTemplate.Id;
    };

export namespace EmailTemplate {
    /**
     * The id of the email template, which can be:
     * - templateName (just the template name)
     * - providerId:templateName (provider-specific template)
     * - providerId:serviceId:templateName (service-specific template)
     */
    export type Id = Brand<string, EmailTemplate>;

    /**
     * The name of the email template.
     */
    export type Name = (typeof templateNames)[number];

    /**
     * Internal ID factory using ts-brand
     */
    export const Id = make<Id>(value => {
        if (!value || value.trim() === "") {
            throw new Error("EmailTemplate: Property 'id' cannot be empty.");
        }
    });

    /**
     * Creates a new email template ID.
     *
     * @param templateName The template name.
     * @param providerId Optional provider ID for provider-specific templates.
     * @param serviceId Optional service ID for service-specific templates.
     * @returns The new email template ID.
     * @throws An error if the template name is invalid or parameters are inconsistent.
     */
    export const createId = (
        templateName: EmailTemplate.Name,
        providerId?: ServiceProvider.Id,
        serviceId?: ServiceDefinition.Id,
        shortId?: string
    ): Id => {
        // Validate template name
        if (!templateNames.includes(templateName)) {
            throw new Error(
                `EmailTemplate: Invalid template name '${templateName}'. ` +
                    `Must be one of: ${templateNames.join(", ")}`
            );
        }

        return Template.createId<Id>(templateName, templateNames, providerId, serviceId, shortId);
    };

    export const Name = (value: string): Name => {
        return Template.Name<Name>(value, templateNames);
    };

    /**
     * Gets the template name from an email template ID.
     *
     * @param id The email template ID.
     * @returns The template name.
     */
    export const getName = (id: Id): EmailTemplate.Name => {
        return Template.parse<Name>(id).templateName;
    };

    export const parse = (
        id: Id
    ): {
        templateName: EmailTemplate.Name;
        providerId?: ServiceProvider.Id;
        serviceId?: ServiceDefinition.Id;
        shortId?: string;
    } => {
        return Template.parse<Name>(id);
    };
}
